'use client';

import React, { useState, useCallback, useEffect } from 'react';
import { Search, Dialog, Button } from 'src/components/PulseUI';
import { ConnectorToggle } from '../../ConnectorToggle';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faPlus,
  faCheck,
  faFileSlash,
} from '@fortawesome/pro-solid-svg-icons';
import { stripHtml } from 'src/utils/stripHtml';
import {
  BaseSelectableItem,
  ConnectorType,
  GenericSelectorModalProps,
} from '../types';

/**
 * Generic selector modal for selecting items.
 * Supports single and multiple selection with connector toggle.
 */
function GenericSelectorModal<T extends BaseSelectableItem>({
  config,
  isOpen,
  selectedItems: initialSelectedItems,
  connector: initialConnector,
  isEditing,
  onClose,
  onSave,
  searchResults,
  isLoading,
  searchTerm,
  onSearchChange,
}: GenericSelectorModalProps<T>) {
  const [selectedItems, setSelectedItems] = useState<T[]>(initialSelectedItems);
  const [connector, setConnector] = useState<ConnectorType>(initialConnector);

  const {
    title,
    searchPlaceholder,
    emptyStateIcon,
    emptyStateMessage,
    noResultsMessage,
    colorTheme,
    selectionMode,
    showConnectorToggle,
    getItemLabel,
    getItemSecondaryLabel,
    getItemKey,
    isItemSelected,
    renderResultsSection,
    itemLabel,
    itemLabelPlural,
  } = config;

  // Tailwind color classes based on theme
  const borderColor = `border-${colorTheme}-200`;
  const textColor = `text-${colorTheme}-800`;
  const bgColor = `bg-${colorTheme}-50`;
  const hoverBgColor = `hover:bg-${colorTheme}-100`;
  const checkBgColor = `bg-${colorTheme}-600`;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedItems(initialSelectedItems);
      setConnector(initialConnector);
      onSearchChange('');
    }
  }, [isOpen, initialSelectedItems, initialConnector, onSearchChange]);

  const addItem = useCallback(
    (item: T) => {
      if (selectionMode === 'single') {
        setSelectedItems([item]);
      } else {
        if (!isItemSelected(item, selectedItems)) {
          setSelectedItems((prev) => [...prev, item]);
        }
      }
    },
    [selectedItems, selectionMode, isItemSelected]
  );

  const removeItem = useCallback(
    (itemToRemove: T) => {
      setSelectedItems((prev) =>
        prev.filter((item) => getItemKey(item) !== getItemKey(itemToRemove))
      );
    },
    [getItemKey]
  );

  const handleSave = useCallback(() => {
    onSave(selectedItems, connector);
  }, [selectedItems, connector, onSave]);

  const getButtonLabel = () => {
    const count = selectedItems.length;
    const label = count === 1 ? itemLabel : itemLabelPlural;

    if (isEditing) {
      if (count === 0) {
        return `Remove All ${itemLabelPlural.charAt(0).toUpperCase() + itemLabelPlural.slice(1)}`;
      }
      return `Update ${count} ${label.charAt(0).toUpperCase() + label.slice(1)}`;
    }
    return `Insert ${count} ${label.charAt(0).toUpperCase() + label.slice(1)}`;
  };

  // Default results renderer
  const renderDefaultResults = () => {
    if (searchTerm) {
      if (searchResults.length > 0) {
        return (
          <div className="h-full overflow-y-auto border border-neutral-200 rounded-lg bg-white">
            {searchResults.map((result, index) => {
              const isSelected = isItemSelected(result, selectedItems);
              const label = getItemLabel(result);
              const secondaryLabel = getItemSecondaryLabel?.(result);

              return (
                <div
                  key={getItemKey(result)}
                  onClick={() => addItem(result)}
                  className={`p-4 border-b border-neutral-100 last:border-b-0 cursor-pointer transition-all duration-300 hover:bg-neutral-50 animate-in slide-in-from-left-1 ${
                    isSelected ? `${bgColor} ${hoverBgColor}` : ''
                  }`}
                  style={{ animationDelay: `${index * 30}ms` }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div
                        className={`font-medium truncate ${
                          isSelected ? textColor : 'text-neutral-900'
                        }`}
                      >
                        {stripHtml(label)}
                      </div>
                      {secondaryLabel && (
                        <div className="text-sm text-neutral-500 truncate">
                          {stripHtml(secondaryLabel)}
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <div
                        className={`flex items-center justify-center w-6 h-6 ${checkBgColor} text-white rounded-full animate-in zoom-in-50 duration-300`}
                      >
                        <Icon icon={faCheck} className="w-3 h-3" />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      }

      // No results found
      return (
        <div className="h-full flex items-center justify-center text-neutral-500 border border-neutral-200 rounded-lg bg-neutral-50/50">
          <div className="text-center animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="flex flex-col items-center gap-3">
              <Icon icon={faFileSlash} className="w-12 h-12 text-neutral-300" />
              <div className="flex flex-col items-center gap-1">
                <div className="text-sm">{noResultsMessage(searchTerm)}</div>
                <div className="text-xs text-neutral-400">
                  Try adjusting your search terms
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // No search term - show initial state
    return (
      <div className="h-full flex items-center justify-center text-neutral-400 border border-neutral-200 rounded-lg bg-neutral-50/50">
        <div className="text-center animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="flex flex-col items-center gap-3">
            <Icon icon={emptyStateIcon} className="w-12 h-12 text-neutral-300" />
            <div className="flex flex-col items-center gap-1">
              <div className="text-sm">Start typing to search for {itemLabelPlural}</div>
              <div className="text-xs">Search by name</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      showCloseButton
      className="h-144"
    >
      <Dialog.Header>
        <h3 className="text-xl font-semibold text-neutral-900">{title}</h3>
      </Dialog.Header>

      <Dialog.Content className="flex flex-col">
        <div className="flex flex-col flex-1 p-6 pt-4 gap-4 h-full">
          {/* Connector Toggle */}
          {showConnectorToggle && (
            <ConnectorToggle value={connector} onChange={setConnector} />
          )}

          {/* Selected items - Fixed height, no jumping */}
          <div>
            <div className="h-24 border border-neutral-200 rounded-lg bg-gradient-to-br from-neutral-50 to-neutral-100 overflow-hidden">
              {selectedItems.length > 0 ? (
                <div className="h-full flex flex-col">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-200 bg-white/80">
                    <span className="text-sm font-medium text-neutral-700">
                      Selected {selectedItems.length === 1 ? itemLabel.charAt(0).toUpperCase() + itemLabel.slice(1) : itemLabelPlural.charAt(0).toUpperCase() + itemLabelPlural.slice(1)}
                    </span>
                    <button
                      onClick={() => setSelectedItems([])}
                      className="text-xs text-neutral-500 hover:text-red-500 transition-colors duration-300"
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-3">
                    <div className="flex flex-wrap gap-2">
                      {selectedItems.filter((item) => item != null).map((item, index) => (
                        <div
                          key={getItemKey(item) ?? index}
                          className={`inline-flex items-center gap-1 bg-white border ${borderColor} ${textColor} px-3 py-1.5 rounded-full text-sm font-medium shadow-sm hover:shadow-md transition-all duration-300 animate-in slide-in-from-left-1`}
                          style={{ animationDelay: `${index * 50}ms` }}
                        >
                          <span>{stripHtml(getItemLabel(item))}</span>
                          <button
                            onClick={() => removeItem(item)}
                            className={`w-4 h-4 flex items-center justify-center text-${colorTheme}-600 hover:text-red-500 hover:bg-red-50 rounded-full transition-all duration-300`}
                            aria-label={`Remove ${stripHtml(getItemLabel(item))}`}
                          >
                            <Icon icon={faXmark} className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-neutral-400">
                  <div className="text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Icon icon={faPlus} className="w-8 h-8 text-neutral-300" />
                      <div className="text-sm">{emptyStateMessage}</div>
                      <div className="text-xs">
                        Search and click to add {itemLabelPlural}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Search */}
          <Search
            placeholder={searchPlaceholder}
            value={searchTerm}
            onSearch={(search) => onSearchChange(search)}
            autoFocus
            className="py-1"
          />

          {/* Results - Take remaining space */}
          <div className="flex-1 overflow-hidden min-h-0">
            {renderResultsSection
              ? renderResultsSection({
                  results: searchResults,
                  selectedItems,
                  onSelect: addItem,
                  onDeselect: removeItem,
                  isLoading,
                  searchTerm,
                })
              : renderDefaultResults()}
          </div>
        </div>
      </Dialog.Content>

      <Dialog.Actions className="justify-between">
        <div className="text-sm text-neutral-600 font-medium flex items-center gap-2">
          <span>
            {selectedItems.length} {selectedItems.length === 1 ? itemLabel : itemLabelPlural}{' '}
            selected
          </span>
          {selectedItems.length > 0 && (
            <div className={`w-2 h-2 bg-${colorTheme}-500 rounded-full animate-pulse`} />
          )}
        </div>
        <div className="flex gap-3">
          <Button variant="outlined" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="solid"
            color={isEditing && selectedItems.length === 0 ? 'danger' : 'primary'}
            onClick={handleSave}
            disabled={!isEditing && selectedItems.length === 0}
          >
            {getButtonLabel()}
          </Button>
        </div>
      </Dialog.Actions>
    </Dialog>
  );
}

export default GenericSelectorModal;
